import { RentalIntake } from "@/models/rental-intake.model"
import { RentalIntakeRepository } from "@/repositories/rental-intake-repository"
import { useRentalIntakeStore } from "@/store/rental-intake.store"
import { type IRentalIntake } from "@/interfaces/api/rental-intake.interface"
import { RentalMutationController } from "@/controllers/model-controllers/abstract/rental-mutation-controller"
import { Project } from "@/models/project.model"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"
import { useRentalDispenseStore } from "@/store/rental-dispense.store"

export default class RentalIntakeController extends RentalMutationController<
  RentalIntake,
  IRentalIntake,
  RentalIntakeRepository
> {
  constructor() {
    const { pushRentalIntakeToRelatedObjects, removeFromRelatedObjects } = useRentalIntakeStore()

    const storeFunctionsConfig = {
      onCreate: pushRentalIntakeToRelatedObjects,
      onDelete: removeFromRelatedObjects
    }

    super(RentalIntake, storeFunctionsConfig, new RentalIntakeRepository())
  }

  public async getReturnableIssuancesList(
    text: string,
    project?: Project
  ): Promise<RentalIntake[]> {
    const data = await super.getReturnableIssuancesListData(text, project)
    const models = await this.storeFunctions.createModel(data)
    const rentalIntakeStore = useRentalIntakeStore()
    rentalIntakeStore.addToReturnableRentalIntakes(models)
    return models
  }

  public async deleteOrRollback(
    instance: RentalIntake,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<boolean> {
    const associatedDispense = instance.associatedRentalDispense

    const { pushRentalIntakeToRelatedObjects } = useRentalIntakeStore()
    const {
      removeFromRelatedObjects: removeDispenseFromRelatedObjects,
      pushToRelatedObjects: pushDispenseToRelatedObjects
    } = useRentalDispenseStore()

    // Optimistically delete the associated rental dispense client-side
    if (associatedDispense) {
      removeDispenseFromRelatedObjects(associatedDispense)
    }

    // Delete the rental intake client-side (optimistic update) and send API request
    const isDeleted = await this.delete(instance, requestConfig, false)

    if (!isDeleted) {
      // Rollback: restore both the rental intake and the associated rental dispense directly via the store
      pushRentalIntakeToRelatedObjects(instance)
      if (associatedDispense) {
        pushDispenseToRelatedObjects(associatedDispense)
      }
    }

    return isDeleted
  }
}
